import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate, formatHoursMinutes } from '../../lib/format';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import { useReportScope } from '../../lib/useReportScope';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const STATUS_META = {
  active:    { label: 'Active',     color: '#38bdf8', bg: 'rgba(56,189,248,0.12)'   },
  completed: { label: 'Completed',  color: '#4ade80', bg: 'rgba(74,222,128,0.12)'   },
  overdue:   { label: 'Overdue',    color: '#f87171', bg: 'rgba(248,113,113,0.12)'  },
  near_end:  { label: 'Near End',   color: '#fbbf24', bg: 'rgba(251,191,36,0.12)'   },
  inactive:  { label: 'Inactive',   color: '#94a3b8', bg: 'rgba(148,163,184,0.10)'  },
};

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || '—', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color,
    }}>
      {m.label}
    </span>
  );
}

function ProgressBar({ pct }) {
  const c = pct === 100 ? '#4ade80' : pct >= 70 ? '#38bdf8' : pct >= 40 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px',
      textAlign: right ? 'right' : 'left',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      whiteSpace: 'nowrap',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

export default function ProjectReports() {
  const { appId } = useParams();
  const { me } = useWorkspace();
  const api = useApi();
  const { isAdmin, scopeIds, isManager } = useReportScope(appId);
  const isMultiUser = isAdmin || isManager;

  const [projects, setProjects]     = useState([]);
  const [members,  setMembers]      = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterMember, setFilterMember] = useState([]);

  useEffect(() => {
    api.listAppMembers(appId)
      .then(r => setMembers(r.items || []))
      .catch(() => {});
  }, [appId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.listProjects(appId, {});
      setProjects(rows || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const nameMap = useMemo(() => {
    const map = {};
    members.forEach(m => {
      map[m.user_id] = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    });
    if (me) map[me.id] = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;
    return map;
  }, [members, me]);

  const memberName = id => (id == null ? '—' : nameMap[id] || `User #${id}`);

  const filtered = useMemo(() => {
    let list = [...projects];

    if (filterStatus === 'active') {
      list = list.filter(p => !['completed', 'inactive', 'deleted'].includes(p.computed_status));
    } else if (filterStatus === 'inactive') {
      list = list.filter(p => p.computed_status === 'inactive' || p.computed_status === 'deleted');
    } else if (filterStatus) {
      list = list.filter(p => p.computed_status === filterStatus);
    }

    if (filterMember.length > 0) {
      list = list.filter(p => filterMember.includes(String(p.owner_user_id)));
    }

    return list;
  }, [projects, filterStatus, filterMember]);

  const summary = useMemo(() => {
    const total     = filtered.length;
    const active    = filtered.filter(p => !['completed', 'inactive', 'deleted'].includes(p.computed_status)).length;
    const completed = filtered.filter(p => p.computed_status === 'completed').length;
    const overdue   = filtered.filter(p => p.computed_status === 'overdue').length;
    const nearEnd   = filtered.filter(p => p.computed_status === 'near_end').length;
    return { total, active, completed, overdue, nearEnd };
  }, [filtered]);

  const pdfTable = useMemo(() => ({
    headers: ['#', 'Project Name', 'Status', 'Progress %', 'Owner', 'Start Date', 'End Date', 'Milestones', 'Tasks Done', 'Time Spent'],
    rows: filtered.map((p, i) => {
      const progress = p.total_milestones > 0 ? (p.milestone_pct || 0) : (p.completion_pct || 0);
      const milestoneDone  = p.milestones_done  ?? p.milestone_done  ?? '—';
      const milestoneTotal = p.total_milestones ?? '—';
      const tasksDone  = p.tasks_done  ?? p.done_tasks  ?? '—';
      const tasksTotal = p.total_tasks ?? '—';
      const mins = p.total_minutes || 0;
      const timeSpent = mins > 0 ? formatHoursMinutes(mins) : '—';
      return [
        i + 1,
        p.name,
        STATUS_META[p.computed_status]?.label || p.computed_status || '—',
        `${progress}%`,
        memberName(p.owner_user_id),
        p.start_date ? formatDate(p.start_date) : '—',
        p.end_date   ? formatDate(p.end_date)   : '—',
        milestoneTotal !== '—' ? `${milestoneDone} / ${milestoneTotal}` : '—',
        tasksTotal    !== '—' ? `${tasksDone} / ${tasksTotal}`          : '—',
        timeSpent,
      ];
    }),
  }), [filtered, nameMap]);

  const pdfCards = useMemo(() => !loading ? [
    { label: 'Total',     value: summary.total,     color: 'var(--text-primary)' },
    { label: 'Active',    value: summary.active,    color: '#38bdf8'             },
    { label: 'Completed', value: summary.completed, color: '#4ade80'             },
    { label: 'Overdue',   value: summary.overdue,   color: '#f87171'             },
    { label: 'Near End',  value: summary.nearEnd,   color: '#fbbf24'             },
  ] : [], [loading, summary]);

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Project Report</h1>
          <p className="tl-subtitle">Overview of all projects — status, progress, ownership, and timelines.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel('project-report.xls', 'Project Report', `Status: ${filterStatus || 'All'}`, pdfTable.headers, pdfTable.rows)}
            disabled={filtered.length === 0}
            style={{ ...excelBtnStyle, opacity: filtered.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Project Report"
            subtitle={`Status: ${filterStatus || 'All'}`}
            summaryCards={pdfCards}
            table={pdfTable}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: 24, padding: '16px 20px',
        background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 160 }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
            <option value="near_end">Near End</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {isMultiUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Project Owner</label>
            <MultiSelectFilter
              options={members
                .filter(m => isAdmin || !scopeIds || scopeIds.has(String(m.user_id)))
                .map(m => ({ value: String(m.user_id), label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email }))}
              selected={filterMember}
              onChange={setFilterMember}
              placeholder="All Members"
              minWidth={200}
            />
          </div>
        )}
      </div>

      {/* Summary cards */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Total',     value: summary.total,     color: 'var(--text-primary)' },
            { label: 'Active',    value: summary.active,    color: '#38bdf8'             },
            { label: 'Completed', value: summary.completed, color: '#4ade80'             },
            { label: 'Overdue',   value: summary.overdue,   color: '#f87171'             },
            { label: 'Near End',  value: summary.nearEnd,   color: '#fbbf24'             },
          ].map(c => (
            <div key={c.label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 110,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No projects found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>Project Name</Th>
                <Th>Status</Th>
                <Th>Progress</Th>
                <Th>Owner</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
                <Th right>Milestones</Th>
                <Th right>Tasks Done</Th>
                <Th right>Time Spent</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
                const progress = p.total_milestones > 0
                  ? (p.milestone_pct || 0)
                  : (p.completion_pct || 0);
                const milestoneDone  = p.milestones_done  ?? p.milestone_done  ?? '—';
                const milestoneTotal = p.total_milestones ?? '—';
                const tasksDone  = p.tasks_done  ?? p.done_tasks  ?? '—';
                const tasksTotal = p.total_tasks ?? '—';
                const mins = p.total_minutes || 0;
                const timeSpent = mins > 0 ? formatHoursMinutes(mins) : '—';
                return (
                  <tr key={p.id} style={{ background: rowBg, borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)', maxWidth: 220, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {p.name}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <StatusBadge status={p.computed_status} />
                    </td>
                    <td style={{ padding: '10px 14px', minWidth: 120 }}>
                      <ProgressBar pct={progress} />
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                      {memberName(p.owner_user_id)}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {p.start_date ? formatDate(p.start_date) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {p.end_date ? formatDate(p.end_date) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {milestoneTotal !== '—' ? `${milestoneDone} / ${milestoneTotal}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: 'var(--brand)', whiteSpace: 'nowrap' }}>
                      {tasksTotal !== '—' ? `${tasksDone} / ${tasksTotal}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {timeSpent}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}